import {
	Grid,
	LoadingOverlay,
	Modal,
	NumberInput,
	Space,
	TextInput,
} from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import {
	IconArrowDown,
	IconArrowUp,
	IconCalculator,
	IconClipboardText,
} from '@tabler/icons-react';
import type { UseQueryResult } from 'react-query';
import { useParams } from 'react-router-dom';
import { SmallModal } from '../../../components/SmallModal';
import { Title } from '../../../components/Title';
import { FormBranchMultiSelector } from '../../../components/form/FormBranchMultiSelector';
import { FormButtons } from '../../../components/form/FormButtons';
import { defaultIconStyle } from '../../../theme/styling/iconStyling';
import { mutate } from '../../../util/api/mutate';
import type { ApiResponse } from '../../../util/types/Response';
import { useUpsertBranchStockForm } from '../hooks/useUpsertBranchStockForm';
import type { UpsertBranchStockDto } from '../types/BranchStockDto';

interface NewBarcodeModalProps {
	close: () => void;
	isOpened: boolean;
	updateProductRecord: () => void;
	skuid: string;
	unavailableBranchQuery: UseQueryResult<ApiResponse<string[]>, unknown>;
}

export function NewBranchStockModal({
	isOpened,
	close,
	updateProductRecord,
	skuid,
	unavailableBranchQuery,
}: NewBarcodeModalProps) {
	const { organisation } = useParams();

	const [
		showSuccessModal,
		{ open: openSuccessModal, close: closeSuccessModal },
	] = useDisclosure(false);

	const { form, inputs } = useUpsertBranchStockForm(skuid);

	const newBranchStockMutation = mutate<UpsertBranchStockDto>(
		`${organisation}/branch-stock/new`,
		inputs,
	);

	const resetAndClose = () => {
		form.reset();
		close();
		closeSuccessModal();
	};

	const complete = () => {
		updateProductRecord();
		resetAndClose();
	};

	return (
		<>
			<Modal
				opened={isOpened}
				onClose={resetAndClose}
				title={<Title label={'New Branch Stock'} />}
				size={'lg'}
				centered
			>
				<LoadingOverlay visible={newBranchStockMutation.isLoading} />
				<SmallModal
					isOpen={showSuccessModal}
					close={complete}
					title={'Branch Stock Added!'}
					body={'A new Branch Stock has been added. Click Ok to continue...'}
				/>
				<Space h={6} />
				<form
					onSubmit={form.onSubmit(async () => {
						await newBranchStockMutation.mutateAsync().then((value) => {
							if (value.isValid) {
								openSuccessModal();
							}
						});
					})}
				>
					<Grid>
						<Grid.Col span={6}>
							<FormBranchMultiSelector
								form={form}
								property={'branchIds'}
								unavailableBranchIds={unavailableBranchQuery.data?.value}
								disabledMessage={'Branch Stock already exists'}
							/>
						</Grid.Col>
						<Grid.Col span={6}>
							<TextInput
								label={'SKUID'}
								leftSection={<IconClipboardText {...defaultIconStyle} />}
								key={form.key('skuid')}
								{...form.getInputProps('skuid')}
								readOnly
								variant="filled"
							/>
						</Grid.Col>
						<Grid.Col span={12}>
							<TextInput
								label={'Description'}
								placeholder={'Insert description...'}
								leftSection={<IconClipboardText {...defaultIconStyle} />}
								key={form.key('description')}
								{...form.getInputProps('description')}
							/>
						</Grid.Col>

						<Grid.Col span={4}>
							<NumberInput
								label={'Current Stock'}
								placeholder={'Insert current stock...'}
								key={form.key('stock')}
								{...form.getInputProps('stock')}
								leftSection={<IconCalculator {...defaultIconStyle} />}
								allowDecimal={false}
								thousandSeparator={','}
							/>
						</Grid.Col>
						<Grid.Col span={4}>
							<NumberInput
								label={'Minimum Stock'}
								placeholder={'Insert minimum stock...'}
								key={form.key('minStock')}
								{...form.getInputProps('minStock')}
								leftSection={<IconArrowDown {...defaultIconStyle} />}
								allowDecimal={false}
								thousandSeparator={','}
							/>
						</Grid.Col>
						<Grid.Col span={4}>
							<NumberInput
								label={'Maximum Stock'}
								placeholder={'Insert maximum stock...'}
								key={form.key('maxStock')}
								{...form.getInputProps('maxStock')}
								leftSection={<IconArrowUp {...defaultIconStyle} />}
								allowDecimal={false}
								thousandSeparator={','}
							/>
						</Grid.Col>
					</Grid>
					<FormButtons close={resetAndClose} />
				</form>
			</Modal>
		</>
	);
}
